import { useApi } from "~/composables/useApi";
import { useToast } from "~/composables/useToast";
import type {
  Task,
  TaskCreateBody,
  TaskListQuery,
  TaskListResponse,
  TaskListScope,
  TaskUpdateAction,
} from "@asap/shared/schemas/task/crud";
import type { TaskSubjectEntity } from "@asap/shared/schemas/task/context";
import { addDays, startOfDay } from "date-fns";

export const TASK_GROUP_IDS = ["overdue", "today", "later", "done"] as const;

export type TaskGroupId = (typeof TASK_GROUP_IDS)[number];

const TASK_LIST_PAGE_SIZE = 50;

export type TaskSubjectFilter = {
  entity: TaskSubjectEntity;
  entityId: string;
};

const toBaseTaskListQuery = (groupId: TaskGroupId, scope: TaskListScope, startOfToday: Date): TaskListQuery => {
  switch (groupId) {
    case "done":
      return {
        scope,
        status: "done",
        pageIndex: 0,
        pageSize: TASK_LIST_PAGE_SIZE,
      };
    case "overdue":
      return {
        scope,
        status: "open",
        due_before: startOfToday.toISOString(),
        pageIndex: 0,
        pageSize: TASK_LIST_PAGE_SIZE,
      };
    case "today":
      return {
        scope,
        status: "open",
        due_from: startOfToday.toISOString(),
        due_before: addDays(startOfToday, 1).toISOString(),
        pageIndex: 0,
        pageSize: TASK_LIST_PAGE_SIZE,
      };
    case "later":
      return {
        scope,
        status: "open",
        due_from: addDays(startOfToday, 1).toISOString(),
        pageIndex: 0,
        pageSize: TASK_LIST_PAGE_SIZE,
      };
    default:
      throw new Error(`Invalid task group: ${groupId}`);
  }
};

export const toTaskListQuery = (
  groupId: TaskGroupId,
  scope: TaskListScope = "assigned_to_me",
  subject?: TaskSubjectFilter
): TaskListQuery => {
  const startOfToday = startOfDay(new Date());
  const subjectQuery = subject ? { subject_entity: subject.entity, subject_id: subject.entityId } : {};
  const query = toBaseTaskListQuery(groupId, scope, startOfToday);

  return { ...query, ...subjectQuery };
};

export const useTasks = () => {
  const { invoke } = useApi();
  const { addToastError, addToastSuccess } = useToast();
  const { t } = useI18n();

  const listTasks = async (query: TaskListQuery): Promise<TaskListResponse> => {
    const result = await invoke({ path: "tasks", method: "GET", query });
    if (!result.success) throw new Error(result.error.message);
    return result.data;
  };

  const countTasksInGroup = async (
    groupId: TaskGroupId,
    scope: TaskListScope,
    subject?: TaskSubjectFilter
  ): Promise<number> => {
    const result = await listTasks({
      ...toTaskListQuery(groupId, scope, subject),
      pageSize: 0,
    });
    return result.totalItems;
  };

  const applyTaskAction = async (taskId: string, action: TaskUpdateAction): Promise<Task> => {
    const result = await invoke({
      path: "tasks/:taskId/:action",
      method: "PUT",
      params: { taskId, action },
    });
    if (!result.success) {
      addToastError({ description: t("tasks.toasts.updateError") });
      throw new Error(result.error.message);
    }
    return result.data;
  };

  const completeTask = async (taskId: string): Promise<Task> => applyTaskAction(taskId, "complete");

  const restoreTask = async (taskId: string): Promise<Task> => applyTaskAction(taskId, "restore");

  const createTask = async (body: TaskCreateBody): Promise<Task> => {
    const result = await invoke({
      path: "tasks",
      method: "POST",
      body,
    });
    if (!result.success) {
      addToastError({ description: t("tasks.toasts.createError") });
      throw new Error(result.error.message);
    }
    addToastSuccess({ description: t("tasks.toasts.createSuccess") });

    return result.data;
  };

  return {
    listTasks,
    countTasksInGroup,
    completeTask,
    restoreTask,
    createTask,
  };
};
